import React, { useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useRecoilState } from 'recoil';
import { useLocales, useTheme } from '../../../../hooks';
import { Box, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import Button from '../../../shared/Button';
import Modal from '../../../shared/Modal';
import { withCheckedModuleIds, withCopiedModules, withSelectedLayoutUIModules } from '../../../../state/Layouts';
import generateId from '../../../../utils/generateId';
import { usePermissionsGuard } from '../../../../hooks/General/usePermissionsGuard';
import { HomepageOptions } from '../../../../state/theme';
import { layoutsViewModuleActionsTestIds } from '../../../shared/TestsIds';
import { useNotifications } from '../../../../hooks/General/useNotifications';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center'
  },
  checkbox: {
    height: 30
  }
}));

export function LayoutViewModuleActions({ isEditable }: { isEditable: boolean }): JSX.Element {
  const { canSave, canDelete } = usePermissionsGuard({
    homepageOption: HomepageOptions.LAYOUTS
  });
  const { classes } = useStyles();
  const { t } = useLocales();
  const { formControlColor } = useTheme();
  const { notifyError, notifySuccess } = useNotifications();

  const [checkedModuleIds, setCheckedModuleIds] = useRecoilState(withCheckedModuleIds);
  const [copiedModules, setCopiedModules] = useRecoilState(withCopiedModules);
  const [selectedLayoutModules, setSelectedLayoutModules] = useRecoilState(withSelectedLayoutUIModules);
  const [isPasteConfirmOpen, setIsPasteConfirmOpen] = useState(false);

  const onCheckboxChange = (evt: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const newCheckedModuleIds: Record<string, boolean> = {};
    if (checked) {
      selectedLayoutModules?.forEach((module) => {
        newCheckedModuleIds[String(module.moduleId)] = true;
      });
    }
    setCheckedModuleIds(newCheckedModuleIds);
  };

  const onCopy = () => {
    const newCopiedModules = getCheckedModules();
    if (!newCopiedModules) return;
    setCopiedModules(newCopiedModules);
    setCheckedModuleIds({}); // Clear the selected items when copying
  };

  const openPasteConfirm = () => {
    if (!copiedModules.length) return;
    setIsPasteConfirmOpen(true);
  };

  const onDelete = () => {
    if (!selectedLayoutModules) return;
    setSelectedLayoutModules(selectedLayoutModules.filter((module) => !checkedModuleIds[String(module.moduleId)]));
    setCheckedModuleIds({}); // Clear the selected items when deleting
  };

  const getCheckedModules = () => {
    return selectedLayoutModules?.filter((module) => checkedModuleIds[String(module.moduleId)]);
  };

  const checkedModules = getCheckedModules();
  const someChecked = checkedModules && checkedModules.length > 0;
  const numChecked = checkedModules?.length || 0;
  const allChecked = someChecked && checkedModules && checkedModules.length === selectedLayoutModules?.length;
  const numCopied = copiedModules.length;
  const pasteConfirmMessage = useMemo(() => t('layouts.paste_confirm', { count: numCopied }), [t, numCopied]);

  const clearCopies = () => setCopiedModules([]);

  const confirmPaste = () => {
    try {
      if (!selectedLayoutModules) return;
      if (!copiedModules.length) return;

      const countToPaste = copiedModules.length;
      setSelectedLayoutModules([
        ...selectedLayoutModules,
        ...copiedModules.map((module) => ({ ...module, moduleId: generateId(), slug: '' }))
      ]);
      notifySuccess(t('layouts.paste_success', { count: countToPaste }));
    } catch (err) {
      notifyError(t('layouts.paste_error'));
    } finally {
      setIsPasteConfirmOpen(false);
    }
  };

  return (
    <div className={classes.root}>
      <FormControlLabel
        className={classes.checkbox}
        control={
          <Checkbox
            color={formControlColor}
            checked={allChecked}
            indeterminate={someChecked && !allChecked}
            disabled={!selectedLayoutModules?.length}
            onChange={onCheckboxChange}
            data-testid={layoutsViewModuleActionsTestIds.selectAllCheckbox}
          />
        }
        label={<Typography variant="body2">{t('general.select_all')}</Typography>}
      />
      <Stack direction="row" gap={2}>
        <Button
          size="small"
          color="grey"
          onClick={onCopy}
          disabled={!someChecked}
          tooltip={t('layouts.copy_modules', { count: numChecked })}
          data-testid={layoutsViewModuleActionsTestIds.copyButton}
        >
          {someChecked ? t('layouts.copy_modules', { count: numChecked }) : t('general.copy')}
        </Button>
        <Button size="small" color="grey" onClick={clearCopies} disabled={!numCopied}>
          {t('layouts.clear_copies')}
        </Button>
        {isEditable && (
          <>
            <Button
              size="small"
              color="primary"
              onClick={openPasteConfirm}
              disabled={!copiedModules.length || !canSave}
              tooltip={t('layouts.paste_modules', { count: copiedModules.length })}
              data-testid={layoutsViewModuleActionsTestIds.pasteButton}
            >
              {copiedModules.length ? t('layouts.paste_modules', { count: copiedModules.length }) : t('general.paste')}
            </Button>
            <Button
              size="small"
              color="error"
              onClick={onDelete}
              disabled={!someChecked || !canDelete}
              tooltip={t('layouts.delete_modules', { count: numChecked })}
              data-testid={layoutsViewModuleActionsTestIds.deleteButton}
            >
              {someChecked ? t('layouts.delete_modules', { count: numChecked }) : t('general.delete')}
            </Button>
          </>
        )}
      </Stack>
      <Modal
        open={isPasteConfirmOpen}
        onClose={() => setIsPasteConfirmOpen(false)}
        width={520}
        height={220}
        headerLeft={t('general.confirm')}
        footerLeft={
          <Button color="grey" onClick={() => setIsPasteConfirmOpen(false)} sx={{ width: 140 }}>
            {t('general.cancel')}
          </Button>
        }
        footerRight={
          <Button color="primary" onClick={confirmPaste} sx={{ width: 140 }}>
            {t('general.paste')}
          </Button>
        }
      >
        <Box p={4}>
          <Typography variant="body2">{pasteConfirmMessage}</Typography>
        </Box>
      </Modal>
    </div>
  );
}
